import React from 'react';

/**
 * 5 力成熟度图谱 —— Page 2 左卡的横向条形图。
 *
 * 入参：vm.abilities（5 力，已按 canonical 顺序）。每项 {name, percentage, l_grade, l_grade_label}。
 *
 * 实现（2026-06）：中性轨道 + 按「所达时期」着色的实色条。
 *  - fill 颜色 = 该力时期（L1 初用 → L4 重构，浅→深），与图例色块一一对应；
 *    长度 = 百分制得分，末端圆点 + 25/50/75 刻度便于读准。
 *  - 时期取自 l_grade（权威；真实数据 percentage=(avg-1)/3*100 与 l_grade 同源、天然对齐），
 *    缺失时按 percentage 分位兜底。
 *  - 主色用 --v5-phase-* token（ReportV5.css 定义），改主色时此处自动跟随。
 *  - print 友好：纯 div+CSS，无 ResizeObserver / animation 依赖，截图无时序问题。
 *
 * 组件名保留 ReportV5Radar，导出名不变，避免改 import。
 */

const PHASES = [
  { idx: 1, label: '初用期', range: '0–25' },
  { idx: 2, label: '惯用期', range: '25–50' },
  { idx: 3, label: '扩散期', range: '50–75' },
  { idx: 4, label: '重构期', range: '75–100' },
];

const L_TO_IDX = { L1: 1, L2: 2, L3: 3, L4: 4 };

// 时期序号：优先取 l_grade（权威），缺失时按 percentage 分位兜底
function phaseIdx(ability) {
  const fromGrade = L_TO_IDX[ability?.l_grade];
  if (fromGrade) return fromGrade;
  const pct = Math.max(0, Math.min(100, Number(ability?.percentage) || 0));
  if (pct < 25) return 1;
  if (pct < 50) return 2;
  if (pct < 75) return 3;
  return 4;
}

export default function ReportV5Radar({ abilities }) {
  if (!abilities || abilities.length === 0) return null;

  return (
    <div className="v5r-hbar-wrap" role="img" aria-label="5 力成熟度图谱">
      {/* 图例：色块=时期填充色，与条形颜色一致 */}
      <div className="v5r-hbar-legend">
        {PHASES.map((p) => (
          <span key={p.idx} className="v5r-hbar-legend-item">
            <i className={`v5r-pf v5r-pf${p.idx}`} />
            {p.label} {p.range}
          </span>
        ))}
      </div>

      {/* 每力一行（包一层 rows 容器：在卡片被 grid 拉高时把行均匀撑满，避免底部留白） */}
      <div className="v5r-hbar-rows">
      {abilities.map((ability) => {
        const pct = Math.max(0, Math.min(100, Number(ability.percentage) || 0));
        const idx = phaseIdx(ability);
        const phaseLabel = ability.l_grade_label || PHASES[idx - 1].label;

        return (
          <div key={ability.code || ability.name} className="v5r-hbar-row">
            {/* 力名 */}
            <div className="v5r-hbar-name">{ability.name}</div>

            {/* 条形轨道（中性底 + 25/50/75 刻度 + 按时期着色的实色条） */}
            <div className="v5r-hbar-track" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
              {[25, 50, 75].map((tick) => (
                <span key={tick} className="v5r-hbar-tick" style={{ left: `${tick}%` }} />
              ))}
              <div className={`v5r-hbar-fill v5r-pf${idx}`} style={{ width: `${pct}%` }} />
            </div>

            {/* 右侧：分值 + 时期名 */}
            <div className="v5r-hbar-meta">
              <span className="v5r-hbar-pct">{pct}</span>
              <span className="v5r-hbar-phase">{phaseLabel}</span>
            </div>
          </div>
        );
      })}
      </div>

      <style>{`
        .v5r-hbar-wrap { padding: 4px 0 6px; }
        /* 卡片被 grid 拉到与右侧阶梯卡等高时，行容器撑满并均匀分布，消除底部留白 */
        .v5r-p2-radar-card { display: flex; flex-direction: column; }
        .v5r-p2-radar-card .v5r-hbar-wrap { flex: 1; display: flex; flex-direction: column; }
        .v5r-p2-radar-card .v5r-hbar-rows { flex: 1; display: flex; flex-direction: column; justify-content: space-between; }
        /* 4 时期填充色：背景=色块/条，color=末端圆点边框（currentColor） */
        .v5r-pf1 { background: var(--v5-phase-1); color: var(--v5-phase-1); }
        .v5r-pf2 { background: var(--v5-phase-2); color: var(--v5-phase-2); }
        .v5r-pf3 { background: var(--v5-phase-3); color: var(--v5-phase-3); }
        .v5r-pf4 { background: var(--v5-phase-4); color: var(--v5-phase-4); }
        .v5r-hbar-legend {
          display: flex;
          gap: 16px;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .v5r-hbar-legend-item {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 600;
          color: var(--v5-ink-2, #2e2a4a);
          letter-spacing: 0.02em;
        }
        .v5r-hbar-legend-item .v5r-pf { width: 11px; height: 11px; border-radius: 3px; flex-shrink: 0; }
        .v5r-hbar-row {
          display: grid;
          grid-template-columns: 56px 1fr 66px;
          align-items: center;
          gap: 12px;
          padding: 9px 0;
        }
        .v5r-hbar-name {
          font-size: 13px;
          color: var(--v5-ink, #1a1730);
          font-weight: 600;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .v5r-hbar-track {
          position: relative;
          height: 18px;
          border-radius: 6px;
          background: #efeff3;
          box-shadow: inset 0 0 0 1px #e4e4ea;
        }
        .v5r-hbar-tick {
          position: absolute;
          top: 0;
          bottom: 0;
          border-left: 1px dashed #cfcfd8;
          z-index: 2;
        }
        .v5r-hbar-fill {
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          border-radius: 6px;
          z-index: 1;
          transition: width 0.5s ease;
        }
        /* 末端圆点：白心 + 时期色描边（currentColor），让条长一眼读准 */
        .v5r-hbar-fill::after {
          content: '';
          position: absolute;
          right: 0;
          top: 50%;
          transform: translate(50%, -50%);
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #fff;
          border: 2.5px solid currentColor;
          box-shadow: 0 1px 3px rgba(58, 47, 176, 0.3);
        }
        .v5r-hbar-meta {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 1px;
        }
        .v5r-hbar-pct {
          font-family: Consolas, 'SF Mono', 'Cascadia Mono', Menlo, monospace;
          font-size: 14px;
          font-weight: 800;
          color: var(--v5-accent-deep, #3a2fb0);
          line-height: 1.2;
        }
        .v5r-hbar-phase {
          font-size: 11px;
          color: var(--v5-muted, #837da6);
          font-weight: 500;
          white-space: nowrap;
        }
        @media print {
          .v5r-hbar-fill { transition: none; }
        }
      `}</style>
    </div>
  );
}
